import React, { useEffect, useMemo, useRef, useState } from "react";
import Styles from "./TrustedContacts.module.scss";
import CommonBgStyles from "../../BackgroundCircles.module.scss";
import { Button, Modal, Select } from "@mtm/components";
import { Col, notification, Row } from "antd";
import {
  ServicesTypesComponentLoad,
  LookupItem,
  GeneralDefinition,
  TrackAnalytics,
} from "../../types/ApplicationTypes";
import {
  formatMobileNumber,
  isEmailValid,
} from "../../helpers/ValidationHelpers";
import {
  UpsertTrustedContactDetails,
  getLookupListByListName,
  getTrustedContactsByPersonId,
} from "../../services/RegistrationService";
import CrossMark from "../../assets/images/CrossMark.png";
import { TrustedContact } from "../../types/PersonTypes";
import CommonStyles from "../MyselfWillUseServices/MyselfWillUseServices.module.scss";
import BackArrow from "../../assets/images/BackArrow.svg";
import { useTripsCtx } from "../../context/TripDetailsContext";
import { usePersonSummaryCtx } from "../../context/PersonSummaryContext";
import { useParams } from "react-router-dom";
import { IServiceUserInfo } from "../../types/ServiceUserInfo";

type Props = {
  pageType: any;
  setPageType: any;
  personSummary: any;
  serviceUserInfo: any;
  isCallFromDashboard?: boolean;
  addTC: boolean;
  setTCVisible?: any;
  setReloadTCData: any;
  editData: TrustedContact;
  isEditTC: boolean;
  setEditTC: any;
  isTCEditFromDashboard: boolean;
  setMakeTCEdit: any;
  setIsBackBtnClick?: Function;
  isTcModal?: boolean;
};

export interface TCInformation {
  firstName: string;
  lastName: string;
  phone: string;
  relationship: string;
  email: string;
  id: number;
  priority: number;
  relationshipName: string;
  order: number;
  personAttributesKeyId: number;
}

const defaultValues = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  relationship: "",
  id: 0,
  priority: 0,
  relationshipName: "",
  order: 0,
  personAttributesKeyId: 0,
};

const TrustedContacts: React.FC<Props> = ({
  setPageType,
  personSummary,
  serviceUserInfo,
  isCallFromDashboard,
  addTC,
  setTCVisible,
  setReloadTCData,
  editData,
  isEditTC,
  setEditTC,
  isTCEditFromDashboard,
  setMakeTCEdit,
  setIsBackBtnClick,
  isTcModal,
}) => {
  const [tcInfo, setTCInfo] = useState<TCInformation>(defaultValues);
  const [relationshipVal, setRelationship] = useState<LookupItem[]>([]);
  const [tcCount, setTCCount] = useState<number>(0);
  const [isDuplicateTrustedContact, setDuplicateTrustedContact] = useState<
    boolean
  >(false);
  const [isValidated, areInputControlsValidated] = useState<boolean>(false);
  const [isValidEmail, setIsValidEmail] = useState<boolean>(true);
  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState<boolean>(true);
  const [isRelationshipDropdownHasData, setRelationshipDropdownCSS] = useState<
    boolean
  >(false);
  const [tcBackCount, setTcBackCount] = useState<number>(0);
  const [isBackEdit, setIsBackEdit] = useState<boolean>(false);
  const [backTcList, setBackTcList] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDuplicateCheck, setIsDuplicateCheck] = useState<boolean>(true);

  const isDuplicateTrustedContactRef = useRef<boolean>();
  const tcCountRef = useRef<number>();
  const isValidatedRef = useRef<boolean>();
  const { serviceDataCtx } = useTripsCtx();
  const [serviceData] = serviceDataCtx;
  const { caregiverSummaryInfo } = usePersonSummaryCtx();

  const params = useParams<{ id: string }>();

  const memoizedUserName = useMemo(() => {
    if (personSummary.whoWillUseServices === "ProfileTC") {
      const profileUserName = serviceData.find(
        (serviceUser: IServiceUserInfo) =>
          serviceUser.personId === Number(params.id)
      )?.firstName;
      return profileUserName;
    }

    const serviceUserName = serviceData[serviceData.length - 1]?.firstName;
    const careGiverName = caregiverSummaryInfo?.firstName;

    return ["ProfileMyself", "Myself"].includes(
      personSummary.whoWillUseServices
    )
      ? careGiverName
      : serviceUserName;
  }, [serviceData]);

  isDuplicateTrustedContactRef.current = isDuplicateTrustedContact;
  tcCountRef.current = tcCount;
  isValidatedRef.current = isValidated;

  useEffect(() => {
    if (isEditTC) {
      setTCInfo(editData);
      setEditTC(false);
    }
  }, [isEditTC]);

  useEffect(() => {
    if (addTC) {
      setRelationshipDropdownCSS(false);
    }
    if (isTCEditFromDashboard || isBackEdit) {
      setRelationshipDropdownCSS(true);
    }
  }, [addTC, isTCEditFromDashboard, isBackEdit]);

  async function handleSkipForNow() {
    redirectToNextPage();
  }

  async function handleCancelForProfilePage() {
    setTCVisible(false);
    ResetPageControls();
    if (isTCEditFromDashboard) {
      setMakeTCEdit(false);
    }
    setIsValidPhoneNumber(true);
    setIsValidEmail(true);
  }

  async function handleSavePlusAddAnother() {
    if (isLoading) {
      // Don't proceed if the button is already in a loading state
      return;
    }

    setIsLoading(true);

    try {
      await saveTrustedContact();
      //reset controls to add another Trusted Contact
      if (
        isValidatedRef.current &&
        !isDuplicateTrustedContactRef.current &&
        !isBackEdit
      ) {
        setTCCount(tcCount + 1);
        ResetPageControls();
      }
      if (isBackEdit) {
        backTcList.length === 4 && setTCCount(tcCount + 1);
        ResetPageControls();
      }
      const personId =
        personSummary.whoWillUseServices === GeneralDefinition.Myself ||
        personSummary.whoWillUseServices === "ProfileTC" ||
        personSummary.whoWillUseServices === "ProfileMyself"
          ? personSummary.personId
          : serviceUserInfo.serviceUserPersonId;
      const trustedContacts = await getTrustedContactsByPersonId(personId);

      setTcBackCount(trustedContacts.length);
      setIsBackEdit(false);
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to add trusted contact details.",
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSave() {
    if (isLoading) {
      // Don't proceed if the button is already in a loading state
      return;
    }

    // Set the loading state to true
    setIsLoading(true);

    try {
      await saveTrustedContact();
      if (isValidatedRef.current && !isDuplicateTrustedContactRef.current) {
        if (personSummary.whoWillUseServices == "ProfileTC") {
          setTCVisible(false);
          setReloadTCData(true);
          ResetPageControls();
          if (isTCEditFromDashboard) {
            setMakeTCEdit(false);
          }
        }

        await redirectToNextPage();
        setIsLoading(false);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }

  function timeout(delay: number) {
    return new Promise((res) => setTimeout(res, delay));
  }

  async function ResetPageControls() {
    await timeout(1000);
    handleTCInfoChange("", "email");
    handleTCInfoChange("", "firstName");
    handleTCInfoChange("", "lastName");
    handleTCInfoChange(0, "relationship");
    handleTCInfoChange("", "phone");
    handleTCInfoChange("", "relationshipName");
    if (isTCEditFromDashboard) {
      setRelationshipDropdownCSS(true);
    } else {
      setRelationshipDropdownCSS(false);
    }
  }

  async function redirectToNextPage() {
    if (
      personSummary.whoWillUseServices === GeneralDefinition.Myself ||
      (personSummary.whoWillUseServices === GeneralDefinition.SomeoneElse &&
        serviceUserInfo.serviceUsersStatus === "Failed")
    ) {
      setPageType(ServicesTypesComponentLoad.WandaPlusSubscription);
    } else if (
      personSummary.whoWillUseServices === GeneralDefinition.SomeoneElse &&
      serviceUserInfo.serviceUsersStatus === "Success"
    ) {
      setPageType(ServicesTypesComponentLoad.AddAnotherServiceUser);
    } else if (
      personSummary.whoWillUseServices === GeneralDefinition.ProfilePage ||
      personSummary.whoWillUseServices === "ProfileMyself"
    ) {
      window.location.hash = "/DashBoard";
    }
  }

  async function handleTCInfoChange(value: any, field: string) {
    if (field === "phone") {
      const output = formatMobileNumber(value);
      setTCInfo((tcInfo) => ({ ...tcInfo, [field]: output }));
    } else if (field === "relationship") {
      setTCInfo((tcInfo) => ({ ...tcInfo, [field]: value }));
      setRelationshipDropdownCSS(true);
    } else {
      setTCInfo((tcInfo) => ({ ...tcInfo, [field]: value }));
    }
  }

  function shouldSaveButtonBeEnable() {
    if (
      tcInfo.firstName &&
      tcInfo.lastName &&
      tcInfo.email &&
      tcInfo.phone &&
      tcInfo.relationship
    ) {
      return true;
    } else {
      return false;
    }
  }

  async function saveTrustedContact() {
    const tcData = {
      personId: Number(
        personSummary.whoWillUseServices === GeneralDefinition.Myself
          ? personSummary.personId
          : personSummary.whoWillUseServices === "ProfileTC" ||
            personSummary.whoWillUseServices === "ProfileMyself"
          ? personSummary.personId
          : serviceUserInfo.serviceUserPersonId
      ),
      trustedContact: [
        {
          firstName: tcInfo.firstName,
          lastName: tcInfo.lastName,
          phone: tcInfo.phone.replace(/-/g, ""),
          phoneAttributes: {
            canReceiveTexts: true,
            notificationOn: true,
            communicationTypes: {
              informative: true,
            },
          },
          email: tcInfo.email,
          emailAttributes: {
            notificationOn: true,
            communicationTypes: {
              informative: true,
            },
          },
          relationship: Number(tcInfo.relationship),
          priority: tcCountRef.current,
          personAttributesKeyId:
            isTCEditFromDashboard || isBackEdit
              ? tcInfo.personAttributesKeyId
              : 0,
          isActive: true,
        },
      ],
    };

    const isValidated = await validateInputControls();
    if (isValidated) {
      //Validate if duplicate Trusted Contact before saving
      const isDuplicate = await isDuplicateContact(tcData.trustedContact);
      if (!isDuplicate || isTCEditFromDashboard || isBackEdit) {
        if (isDuplicate) {
          setDuplicateTrustedContact(isDuplicate);
          setIsDuplicateCheck(false);
        } else {
          const personResult = await UpsertTrustedContactDetails(tcData);
          setIsDuplicateCheck(true);
        }
      } else if (isDuplicate && isCallFromDashboard) {
        ResetPageControls();
      }
      if (!isTCEditFromDashboard && !isBackEdit) {
        setDuplicateTrustedContact(isDuplicate);
      }
      TrackAnalytics(
        "/DashBoard",
        "Trusted Contact added",
        "Trusted Contact Page"
      );
    }
  }

  async function validateInputControls() {
    const isValidEmail = isEmailValid(tcInfo.email);
    if (isValidEmail) {
      setIsValidEmail(true);
    } else {
      setIsValidEmail(false);
    }

    const isValidPhoneNumber = tcInfo.phone.length === 12 ? true : false;
    if (isValidPhoneNumber) {
      setIsValidPhoneNumber(true);
    } else {
      setIsValidPhoneNumber(false);
    }

    let validated = isValidEmail && isValidPhoneNumber ? true : false;
    areInputControlsValidated(validated);
    return validated;
  }

  async function isDuplicateContact(tcData: any) {
    const personId =
      personSummary.whoWillUseServices === GeneralDefinition.Myself ||
      personSummary.whoWillUseServices === "ProfileTC" ||
      personSummary.whoWillUseServices === "ProfileMyself"
        ? personSummary.personId
        : serviceUserInfo.serviceUserPersonId;
    const previousTrustedContacts = await getTrustedContactsByPersonId(
      personId
    );
    const duplicateRecord = await getMatchingRecord(
      tcData[0],
      previousTrustedContacts
    );
    return duplicateRecord !== undefined ? true : false;
  }

  function getMatchingRecord(tcData: any, previousTrustedContacts: any) {
    return previousTrustedContacts.find((tcRecord: any) => {
      return (
        tcRecord.firstName.toString().toLowerCase() ===
          tcData.firstName.toString().toLowerCase() &&
        tcRecord.lastName.toString().toLowerCase() ===
          tcData.lastName.toString().toLowerCase() &&
        tcRecord.email.toString().toLowerCase() ===
          tcData.email.toString().toLowerCase() &&
        tcRecord.phone === tcData.phone
      );
    });
  }

  useEffect(() => {
    getRelationshipList();
    getTrustedContactsCount();
  }, []);

  async function getRelationshipList() {
    let relationshipVal = await getLookupListByListName(
      1,
      "TrustedContactRelationships"
    );
    relationshipVal.map(
      (x: { itemId: string }) => (x.itemId = x.itemId.toString())
    );
    setRelationship(relationshipVal);
  }

  async function getTrustedContactsCount() {
    const personId =
      personSummary.whoWillUseServices === GeneralDefinition.Myself ||
      personSummary.whoWillUseServices === "ProfileTC" ||
      personSummary.whoWillUseServices === "ProfileMyself"
        ? personSummary.personId
        : serviceUserInfo.serviceUserPersonId;
    let count = await getTrustedContactsByPersonId(personId);
    setTCCount(count.length);
  }

  function handleSuccess() {
    setDuplicateTrustedContact(false);
  }
  function handleCancel() {
    setDuplicateTrustedContact(false);
  }

  function handleSomeOneElse() {
    setDuplicateTrustedContact(false);
    ResetPageControls();
  }

  const [width] = useWindowSize();

  function useWindowSize() {
    const [size, setSize] = useState([window.innerWidth]);
    useEffect(() => {
      const handleResize = () => {
        setSize([window.innerWidth]);
      };
      window.addEventListener("resize", handleResize);
    }, []);
    return size;
  }

  async function getBackTrustedContact() {
    try {
      const personId =
        personSummary.whoWillUseServices === GeneralDefinition.Myself ||
        personSummary.whoWillUseServices === "ProfileTC" ||
        personSummary.whoWillUseServices === "ProfileMyself"
          ? personSummary.personId
          : serviceUserInfo.serviceUserPersonId;
      const trustedContacts = await getTrustedContactsByPersonId(personId);
      const lastTc = trustedContacts[tcBackCount - 1];
      const lastTcInfo = {
        firstName: lastTc.firstName,
        lastName: lastTc.lastName,
        phone: formatMobileNumber(lastTc.phone),
        email: lastTc.email,
        relationship: lastTc.relationship.toString(),
        id: 0,
        priority: lastTc.priority,
        relationshipName: lastTc.relationshipName,
        order: 0,
        personAttributesKeyId: lastTc.personAttributesKeyId,
      };
      setBackTcList(trustedContacts);
      setTCInfo(lastTcInfo);
      setIsBackEdit(true);
      if (tcBackCount > 0) {
        setTcBackCount(tcBackCount - 1);
      }
      if (tcCount >= 4) {
        setTCCount(tcCount - 1);
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          "Failed to fetch trusted contact details. Please try again later.",
      });
    }
  }

  function handleBackToPrevious() {
    setIsBackBtnClick && setIsBackBtnClick(true);
    if (tcBackCount === 0) {
      ["ProfileMyself", "Myself"].includes(personSummary.whoWillUseServices)
        ? setPageType(ServicesTypesComponentLoad.MyselfWillUseServices)
        : setPageType(ServicesTypesComponentLoad.SomeoneElseWillUseServices);
    } else {
      getBackTrustedContact();
    }
  }

  return (
    <Row>
      <div
        className={
          isRelationshipDropdownHasData
            ? "container" + " " + Styles.dark
            : "container" + " " + Styles.opaque
        }
      >
        <div className={CommonBgStyles.bg_circle} />
        <div
          className={
            "row " +
            (isCallFromDashboard ? Styles.modalRowPadLeft : Styles.rowPafLeft)
          }
        >
          {!isTcModal && (
            <Col className={CommonStyles.backOption}>
              <Button
                link={true}
                clickHandler={handleBackToPrevious}
                className={CommonStyles.backOptionButton}
              >
                <img
                  src={BackArrow}
                  alt="Back arrow button to navigate to previous page"
                />
                Back
              </Button>
            </Col>
          )}

          {!isCallFromDashboard ? (
            <>
              <Col span={24} className={Styles.headingText}>
                Scheduling Services for{" "}
                {personSummary.whoWillUseServices ===
                  GeneralDefinition.Myself ||
                personSummary.whoWillUseServices === "ProfileMyself"
                  ? "Myself"
                  : "Someone Else"}
              </Col>
              <Col
                span={24}
                className={`${Styles.padLeft} ${Styles.addTrustedContactHeading}`}
              >
                Add {tcCount === 0 ? "a" : "another"} trusted contact for
                {personSummary.whoWillUseServices ===
                  GeneralDefinition.Myself ||
                personSummary.whoWillUseServices === "ProfileMyself"
                  ? " yourself"
                  : " " + serviceUserInfo.serviceUserFirstName}
                ?
              </Col>
            </>
          ) : (
            <Col span={24} className={Styles.modalAddTCHeading}>
              {isTCEditFromDashboard ? (
                <p>Edit Trusted Contact</p>
              ) : (
                <p>Add Trusted Contact</p>
              )}
            </Col>
          )}
          <Col
            span={24}
            className={`subTextFont ${
              isCallFromDashboard
                ? Styles.modalAddTrustedContactText
                : `${Styles.padLeft} ${Styles.addTrustedContactText}`
            }`}
          >
            A trusted contact is someone other than the caregiver who will also
            receive communications about {memoizedUserName}'s transportation &
            other services.
          </Col>
          <Col
            span={24}
            className={`subTextFont ${
              isCallFromDashboard
                ? `${Styles.modalPaddingInput} ${Styles.tcInput}`
                : `${Styles.paddingInput} ${Styles.tcInput}`
            }`}
          >
            <input
              type="Text"
              placeholder="First Name"
              onChange={(e) => {
                handleTCInfoChange(e.target.value, "firstName");
              }}
              value={tcInfo.firstName}
              disabled={isTCEditFromDashboard && isDuplicateCheck}
              className={
                isTCEditFromDashboard && isDuplicateCheck
                  ? Styles.disabledFields
                  : undefined
              }
            />
          </Col>
          <Col
            span={24}
            className={`subTextFont ${
              isCallFromDashboard
                ? `${Styles.modalPaddingInput} ${Styles.tcInput}`
                : `${Styles.paddingInput} ${Styles.tcInput}`
            }`}
          >
            <input
              type="Text"
              placeholder="Last Name"
              onChange={(e) => {
                handleTCInfoChange(e.target.value, "lastName");
              }}
              value={tcInfo.lastName}
              disabled={isTCEditFromDashboard && isDuplicateCheck}
              className={
                isTCEditFromDashboard && isDuplicateCheck
                  ? Styles.disabledFields
                  : undefined
              }
            />
          </Col>
          <Col
            span={24}
            className={`subTextFont ${
              isCallFromDashboard
                ? `${Styles.modalPadLeft} ${Styles.tcInput}`
                : `${Styles.padLeft} ${Styles.tcInput}`
            }`}
          >
            <input
              type="Text"
              placeholder="Phone Number"
              onChange={(e) => {
                handleTCInfoChange(e.target.value, "phone");
              }}
              value={tcInfo.phone}
            />
          </Col>
          <Col
            span={24}
            className={`subTextFont ${
              isCallFromDashboard
                ? `${Styles.validationError} ${Styles.modalValidationError}`
                : `${Styles.validationError} ${Styles.padLeft}`
            }`}
          >
            {!isValidPhoneNumber && (
              <span>Please enter a valid phone number.</span>
            )}
          </Col>
          <Col
            span={24}
            className={`subTextFont ${
              isCallFromDashboard
                ? `${Styles.modalPadLeft} ${Styles.tcInput}`
                : `${Styles.padLeft} ${Styles.tcInput}`
            }`}
          >
            <input
              type="Text"
              placeholder="Email Address"
              onChange={(e) => {
                handleTCInfoChange(e.target.value, "email");
              }}
              value={tcInfo.email}
            />
          </Col>
          <Col
            span={24}
            className={`subTextFont ${
              isCallFromDashboard
                ? `${Styles.validationError} ${Styles.modalValidationError}`
                : `${Styles.validationError} ${Styles.padLeft}`
            }`}
          >
            {!isValidEmail && <span>Please enter a valid email address.</span>}
          </Col>
          <Col
            span={24}
            className={`subTextFont ${
              isCallFromDashboard
                ? isTCEditFromDashboard
                  ? `${Styles.modalPadLeft} ${Styles.relationship} ${Styles.modalRelationship}`
                  : `${Styles.modalPadLeft} ${Styles.relationship}`
                : `${Styles.padLeft} ${Styles.relationship}`
            }`}
          >
            <Select
              isButtonStyle={true}
              placeholder="Relationship"
              options={relationshipVal.map((r: LookupItem) => ({
                id: r.itemId,
                display: r.itemName,
              }))}
              onSelectionChange={(id) => {
                handleTCInfoChange(id, "relationship");
              }}
              value={
                Array.from(relationshipVal).find(
                  (option) => option.itemId === tcInfo.relationship
                )?.itemId
              }
            />
          </Col>
          {isCallFromDashboard ? (
            <Col
              span={24}
              sm={20}
              md={20}
              lg={20}
              id={Styles.modalDashboardBtn}
            >
              <div>
                <Button
                  clickHandler={(e) => handleCancelForProfilePage()}
                  className={`${Styles.buttonSkipForNow} ${Styles.modalbtnWidth}`}
                >
                  Cancel
                </Button>
              </div>
              <div>
                {isTCEditFromDashboard ? (
                  <Button
                    disabled={isLoading}
                    clickHandler={(e) => handleSave()}
                    className={`${Styles.buttonSave} ${Styles.modalbtnWidth} `}
                    name="buttonSave"
                  >
                    <div>Save Changes</div>
                  </Button>
                ) : (
                  <Button
                    disabled={isLoading || !shouldSaveButtonBeEnable()}
                    clickHandler={(e) => handleSave()}
                    className={`${Styles.buttonSave} ${Styles.modalbtnWidth} ${
                      !shouldSaveButtonBeEnable()
                        ? "wandaBtnDisabled"
                        : "wandaBtnEnabled"
                    }`}
                    name="buttonSave"
                  >
                    <div>Save</div>
                  </Button>
                )}
              </div>
            </Col>
          ) : (
            <>
              {width > GeneralDefinition.MobileView && tcCount < 4 && (
                <>
                  <Col
                    span={24}
                    sm={24}
                    md={24}
                    lg={8}
                    className={Styles.padTop}
                  >
                    <Button
                      clickHandler={(e) => handleSkipForNow()}
                      className={Styles.buttonSkipForNow}
                    >
                      Skip For Now
                    </Button>
                  </Col>

                  <Col span={24} lg={6}>
                    <Button
                      disabled={!shouldSaveButtonBeEnable() || isLoading}
                      clickHandler={(e) => handleSavePlusAddAnother()}
                      name="buttonAddAnother"
                      className={
                        Styles.buttonAddAnother +
                        " " +
                        (!shouldSaveButtonBeEnable()
                          ? "wandaBtnDisabled"
                          : "wandaBtnEnabled")
                      }
                    >
                      Save + Add Another
                    </Button>
                  </Col>

                  <Col span={24} lg={8}>
                    <Button
                      disabled={!shouldSaveButtonBeEnable()}
                      clickHandler={(e) => handleSave()}
                      className={
                        Styles.buttonSave +
                        " " +
                        (!shouldSaveButtonBeEnable()
                          ? "wandaBtnDisabled"
                          : "wandaBtnEnabled")
                      }
                      name="buttonSave"
                    >
                      Save
                    </Button>
                  </Col>
                </>
              )}
              {width > GeneralDefinition.MobileView && tcCount >= 4 && (
                <>
                  <Col span={24} lg={10} className={Styles.skipForNow}>
                    <Button
                      clickHandler={(e) => handleSkipForNow()}
                      className={Styles.buttonSkipForNow}
                    >
                      Skip For Now
                    </Button>
                  </Col>
                  <Col span={24} lg={12} className={Styles.saveButton}>
                    <Button
                      disabled={!shouldSaveButtonBeEnable()}
                      clickHandler={(e) => handleSave()}
                      name="buttonSave"
                      className={
                        Styles.buttonSave +
                        " " +
                        (!shouldSaveButtonBeEnable()
                          ? "wandaBtnDisabled"
                          : "wandaBtnEnabled")
                      }
                    >
                      Save
                    </Button>
                  </Col>
                </>
              )}
              {width <= GeneralDefinition.MobileView && tcCount < 4 && (
                <>
                  <Col
                    span={24}
                    lg={8}
                    className={Styles.buttonSkipForNowPadding}
                  >
                    <Button
                      clickHandler={(e) => handleSkipForNow()}
                      className={Styles.buttonSkipForNow}
                    >
                      Skip For Now
                    </Button>
                  </Col>

                  <Col span={24} lg={8} className={Styles.buttonSavePadding}>
                    <Button
                      disabled={!shouldSaveButtonBeEnable()}
                      clickHandler={(e) => handleSave()}
                      name="buttonSave"
                      className={
                        Styles.buttonSave +
                        " " +
                        (!shouldSaveButtonBeEnable()
                          ? "wandaBtnDisabled"
                          : "wandaBtnEnabled")
                      }
                    >
                      Save
                    </Button>
                  </Col>

                  <Col
                    span={24}
                    lg={6}
                    className={Styles.buttonSavePlusAddPadding}
                  >
                    <Button
                      disabled={!shouldSaveButtonBeEnable() || isLoading}
                      clickHandler={(e) => handleSavePlusAddAnother()}
                      name="buttonAddAnother"
                      className={
                        Styles.buttonAddAnother +
                        " " +
                        (!shouldSaveButtonBeEnable()
                          ? "wandaBtnDisabled"
                          : "wandaBtnEnabled")
                      }
                    >
                      Save + Add Another
                    </Button>
                  </Col>
                </>
              )}
              {width <= GeneralDefinition.MobileView && tcCount >= 4 && (
                <>
                  <Col
                    span={24}
                    lg={10}
                    className={Styles.buttonSkipForNowPadding}
                  >
                    <Button
                      clickHandler={(e) => handleSkipForNow()}
                      className={Styles.buttonSkipForNow}
                    >
                      Skip For Now
                    </Button>
                  </Col>
                  <Col span={24} lg={12} className={Styles.buttonSavePadding}>
                    <Button
                      disabled={!shouldSaveButtonBeEnable()}
                      clickHandler={(e) => handleSave()}
                      name="buttonSave"
                      className={
                        Styles.buttonSave +
                        " " +
                        (!shouldSaveButtonBeEnable()
                          ? "wandaBtnDisabled"
                          : "wandaBtnEnabled")
                      }
                    >
                      Save
                    </Button>
                  </Col>
                </>
              )}
            </>
          )}
          <div>
            <Modal
              handleSuccess={handleSuccess}
              handleCancel={handleCancel}
              visible={isDuplicateTrustedContact}
              okButtonProps={{ hidden: true }}
              cancelButtonProps={{ hidden: true }}
              width={547}
              centered={true}
            >
              <img
                src={CrossMark}
                alt="CrossMark"
                className="modalPopUpCloseIcon"
                onClick={handleCancel}
              />
              <div className="duplicateContactHeadingText">
                Duplicate Trusted Contact
              </div>
              <div className="duplicateContactText subTextFont">
                This person is already set up to receive information about
                <label className="duplicateContactTextBold subTextFont">
                  {personSummary.whoWillUseServices ===
                    GeneralDefinition.Myself ||
                  personSummary.whoWillUseServices === "ProfileTC" ||
                  personSummary.whoWillUseServices === "ProfileMyself"
                    ? " " + personSummary.firstName
                    : " " + serviceUserInfo.serviceUserFirstName}
                  's
                </label>
                services.
              </div>
              <div className="duplicateContactText subTextFont">
                Would you like to add someone else?
              </div>
              {width > GeneralDefinition.MobileView && (
                <div className="tcModalButtonAlign">
                  <div className="tcModalButton">
                    <Button
                      className="cancelModalBtn"
                      key="close"
                      clickHandler={handleCancel}
                    >
                      Cancel
                    </Button>
                  </div>
                  <div className="tcModalButtonAddSomeone">
                    <Button
                      className="saveBtn"
                      key="close"
                      clickHandler={handleSomeOneElse}
                    >
                      Add Someone Else
                    </Button>
                  </div>
                </div>
              )}
              {width <= GeneralDefinition.MobileView && (
                <div className="tcContactButtonAlignMobile">
                  <div className="tcModalButtonAddSomeone">
                    <Button
                      className="cancelModalBtn"
                      key="close"
                      clickHandler={handleCancel}
                    >
                      Cancel
                    </Button>
                  </div>
                  <br />
                  <div className="tcModalButtonAddSomeone">
                    <Button
                      className="saveBtn"
                      key="close"
                      clickHandler={handleSomeOneElse}
                    >
                      Add Someone Else
                    </Button>
                  </div>
                </div>
              )}
            </Modal>
          </div>
        </div>
      </div>
    </Row>
  );
};
export default TrustedContacts;
